import { authConfigsClient } from '~/services';
import { ref, watch, computed } from 'vue';
import { createGlobalState } from '@vueuse/core';

const defaultData = () => ({ auth_config_type: 'custom_header', friendly_name: '', header_name: '', header_value: '' });

export const useAuthConfigForm = createGlobalState(() => {
    const teamId = Spark.state.currentTeam.id;

    const originalData = ref(defaultData());
    const data = ref(defaultData());
    const success = ref(false);
    const submitting = ref(false);
    const loading = ref(false);

    watch(
        () => data.value?.auth_config_id,
        async (id) => {
            success.value = false;
            submitting.value = false;
            loading.value = true;

            if (id) {
                originalData.value = (await authConfigsClient.get(teamId, id)).data;
                data.value = { ...originalData.value };
            } else {
                originalData.value = defaultData();
                data.value = defaultData();
            }
            loading.value = false;
        },
        { immediate: true }
    );

    const unmount = () => {
        originalData.value = defaultData();
        data.value = defaultData();
        success.value = false;
        submitting.value = false;
        loading.value = false;
    };

    const payload = computed(() => {
        const allowedKeys =
            data.value.auth_config_type === 'oauth2_client_credentials'
                ? ['token_endpoint', 'client_id', 'client_secret']
                : ['header_name', 'header_value'];

        const params = Object.keys(data.value)
            .filter((key) => data.value[key] !== originalData.value[key] && allowedKeys.includes(key))
            .reduce(
                (modified, key) => ({
                    ...modified,
                    [key]: data.value[key],
                }),
                {}
            );

        params.auth_config_type = data.value.auth_config_type;
        params.friendly_name = data.value.friendly_name;

        if (data.value.auth_config_type === 'custom_header' && (params.header_name || params.header_value)) {
            params.header_name = data.value.header_name;
            params.header_value = data.value.header_value;
        } else if (
            data.value.auth_config_type === 'oauth2_client_credentials' &&
            (params.client_secret || params.client_id)
        ) {
            params.token_endpoint = data.value.token_endpoint;
            params.client_id = data.value.client_id;
            params.client_secret = data.value.client_secret;
        }

        return params;
    });

    const submit = async () => {
        if (submitting.value) {
            return;
        }

        submitting.value = true;

        try {
            if (data.value.auth_config_id) {
                const response = (await authConfigsClient.update(teamId, data.value.auth_config_id, payload.value))
                    .data;
                success.value = true;

                return response;
            } else {
                const response = (await authConfigsClient.create(teamId, payload.value)).data;
                success.value = true;

                return response;
            }
        } catch (e) {
            success.value = false;
            throw e;
        } finally {
            submitting.value = false;
        }
    };

    return { submitting, loading, submit, data, payload, success, unmount, defaultData };
});
